/**
 * useInference React Hook
 *
 * Hook for running travel context inference in React components.
 * Combines signal collection with inference API and caching.
 *
 * Part 4 update: Uses session storage cache and inference cookie
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { useSignalCollection } from './useSignalCollection';
import { getApiUrl } from '../utils/apiConfig';

// =============================================================================
// TYPES
// =============================================================================

/**
 * Recommended country from inference
 */
interface RecommendedCountry {
  countryCode: string;
  countryName: string;
  rank: number;
  score: number;
  reasons: string[];
  flags: {
    isHomeCountry: boolean;
    isCurrentLocation: boolean;
    fromReferrer: boolean;
    fromUtm: boolean;
    fromHistory: boolean;
    seasonalBoost: boolean;
  };
}

/**
 * Inference result shape
 */
interface InferenceResult {
  onTrip: 'yes' | 'no' | 'maybe';
  homeCountry: string | null;
  currentCountry: string | null;
  relevantCountries: RecommendedCountry[];
  overallConfidence: 'high' | 'medium' | 'low';
  exclusions: {
    countries: string[];
    reason: string | null;
  };
}

/**
 * Cached inference entry
 */
interface CachedInference {
  result: InferenceResult;
  signalsHash: string;
  computedAt: number;
  expiresAt: number;
}

/**
 * Hook options
 */
interface UseInferenceOptions {
  /** Auto-run inference when signals are collected */
  autoRun?: boolean;
  /** Maximum countries to return */
  maxCountries?: number;
  /** Cache duration in seconds */
  cacheDurationSeconds?: number;
  /** Force refresh on mount */
  forceRefresh?: boolean;
  /** Include debug info in results */
  includeDebug?: boolean;
}

/**
 * Hook return type
 */
interface UseInferenceReturn {
  /** Inference result */
  result: InferenceResult | null;
  /** Loading state */
  isLoading: boolean;
  /** Error if any */
  error: Error | null;
  /** Where result came from */
  source: 'cache' | 'computed' | null;
  /** Re-run inference */
  refresh: () => Promise<void>;
  /** Clear cached result */
  clear: () => void;
  /** Quick helpers */
  isOnTrip: boolean;
  isProbablyOnTrip: boolean;
  homeCountry: string | null;
  topCountry: string | null;
  topCountries: string[];
}

// =============================================================================
// CONSTANTS
// =============================================================================

const COOKIE_NAME = 'jetogo_ctx';
const SESSION_KEY = 'jetogo_inference';
const DEFAULT_CACHE_DURATION = 3600; // 1 hour in seconds
const COOKIE_MAX_AGE_DAYS = 30;

// =============================================================================
// CACHE UTILITIES
// =============================================================================

/**
 * Simple hash for signal comparison
 */
function hashSignals(signals: Record<string, unknown>): string {
  const key = [
    signals.ipCountry ?? '',
    signals.timezoneCountry ?? '',
    signals.localeCountry ?? '',
    signals.referrerCountryHint ?? '',
    signals.destinationParam ?? '',
    signals.deviceType ?? '',
  ].join('|');

  let hash = 5381;
  for (let i = 0; i < key.length; i++) {
    hash = ((hash << 5) + hash) + key.charCodeAt(i);
  }
  return (hash >>> 0).toString(36);
}

/**
 * Get cached inference from session storage
 */
function getSessionCache(): CachedInference | null {
  if (typeof window === 'undefined') return null;

  try {
    const value = sessionStorage.getItem(SESSION_KEY);
    if (!value) return null;
    return JSON.parse(value) as CachedInference;
  } catch {
    return null;
  }
}

/**
 * Set session cache
 */
function setSessionCache(
  result: InferenceResult,
  signals: Record<string, unknown>,
  durationSeconds: number
): void {
  if (typeof window === 'undefined') return;

  try {
    const cached: CachedInference = {
      result,
      signalsHash: hashSignals(signals),
      computedAt: Date.now(),
      expiresAt: Date.now() + durationSeconds * 1000,
    };
    sessionStorage.setItem(SESSION_KEY, JSON.stringify(cached));
  } catch {
    // Ignore storage errors
  }
}

/**
 * Clear session cache
 */
function clearSessionCache(): void {
  if (typeof window === 'undefined') return;
  try {
    sessionStorage.removeItem(SESSION_KEY);
  } catch {
    // Ignore
  }
}

/**
 * Set inference cookie
 */
function setInferenceCookie(result: InferenceResult, ipCountry: string | null): void {
  if (typeof document === 'undefined') return;

  try {
    const data: InferenceCookie = {
      home: result.homeCountry,
      trip: result.onTrip,
      ip: ipCountry,
      ts: Math.floor(Date.now() / 1000),
    };
    const value = btoa(JSON.stringify(data));
    const maxAge = COOKIE_MAX_AGE_DAYS * 24 * 60 * 60;
    document.cookie = `${COOKIE_NAME}=${value}; path=/; max-age=${maxAge}; SameSite=Lax`;
  } catch {
    // Ignore cookie errors
  }
}

/**
 * Clear inference cookie
 */
function clearInferenceCookie(): void {
  if (typeof document === 'undefined') return;
  try {
    document.cookie = `${COOKIE_NAME}=; path=/; max-age=0; SameSite=Lax`;
  } catch {
    // Ignore
  }
}

/**
 * Check if cache is valid
 */
function isCacheValid(
  cached: CachedInference,
  currentSignals: Record<string, unknown>
): boolean {
  // Expired?
  if (Date.now() > cached.expiresAt) {
    return false;
  }

  // Signals changed?
  const currentHash = hashSignals(currentSignals);
  if (currentHash !== cached.signalsHash) {
    return false;
  }

  return true;
}

// =============================================================================
// HOOK IMPLEMENTATION
// =============================================================================

/**
 * Hook for travel context inference with caching
 */
export function useInference(options: UseInferenceOptions = {}): UseInferenceReturn {
  const {
    autoRun = true,
    maxCountries = 5,
    cacheDurationSeconds = DEFAULT_CACHE_DURATION,
    forceRefresh = false,
    includeDebug = false,
  } = options;

  const [result, setResult] = useState<InferenceResult | null>(null);
  const [source, setSource] = useState<'cache' | 'computed' | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const abortControllerRef = useRef<AbortController | null>(null);
  const hasRun = useRef(false);

  // Get signals from signal collection hook
  const { signals, isLoading: signalsLoading, error: signalError } = useSignalCollection({
    autoCollect: true,
  });

  /**
   * Run inference (with cache check)
   */
  const runInference = useCallback(async (bypassCache: boolean = false) => {
    if (!signals) return;

    // Check cache first (unless bypassing)
    if (!bypassCache) {
      const cached = getSessionCache();
      if (cached && isCacheValid(cached, signals)) {
        setResult(cached.result);
        setSource('cache');
        setIsLoading(false);
        return;
      }
    }

    // Abort previous request if any
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();

    setIsLoading(true);
    setError(null);

    try {
      const response = await fetch(getApiUrl('/inference'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          signals,
          maxCountries,
          includeDebug,
        }),
        signal: abortControllerRef.current.signal,
      });

      if (!response.ok) {
        throw new Error(`Inference failed: ${response.status}`);
      }

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.error || 'Inference failed');
      }

      // Update caches
      setSessionCache(data.result, signals, cacheDurationSeconds);
      setInferenceCookie(data.result, signals.ipCountry ?? null);

      setResult(data.result);
      setSource('computed');
    } catch (err) {
      if (err instanceof Error && err.name === 'AbortError') {
        return;
      }
      setError(err instanceof Error ? err : new Error('Inference failed'));
    } finally {
      setIsLoading(false);
    }
  }, [signals, maxCountries, includeDebug, cacheDurationSeconds]);

  /**
   * Clear all caches
   */
  const clear = useCallback(() => {
    clearSessionCache();
    clearInferenceCookie();
    setResult(null);
    setSource(null);
  }, []);

  /**
   * Force refresh
   */
  const refresh = useCallback(async () => {
    await runInference(true);
  }, [runInference]);

  // Auto-run when signals are collected
  useEffect(() => {
    if (autoRun && signals && !signalsLoading && !hasRun.current) {
      hasRun.current = true;
      runInference(forceRefresh);
    }
  }, [autoRun, signals, signalsLoading, runInference, forceRefresh]);

  // Propagate signal collection errors
  useEffect(() => {
    if (signalError) {
      setError(signalError);
    }
  }, [signalError]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  // Computed values
  const isOnTrip = result?.onTrip === 'yes';
  const isProbablyOnTrip = result?.onTrip === 'yes' || result?.onTrip === 'maybe';
  const homeCountry = result?.homeCountry ?? null;
  const topCountry = result?.relevantCountries[0]?.countryCode ?? null;
  const topCountries = result?.relevantCountries.map((c) => c.countryCode) ?? [];

  return {
    result,
    isLoading: isLoading || signalsLoading,
    error: error || signalError,
    source,
    refresh,
    clear,
    isOnTrip,
    isProbablyOnTrip,
    homeCountry,
    topCountry,
    topCountries,
  };
}

// =============================================================================
// CONVENIENCE HOOKS
// =============================================================================

/**
 * Hook for just getting trip status
 */
export function useTripStatus(): {
  isOnTrip: boolean;
  isProbablyOnTrip: boolean;
  status: 'yes' | 'no' | 'maybe' | null;
  isLoading: boolean;
  source: 'cache' | 'computed' | null;
} {
  const { result, isLoading, isOnTrip, isProbablyOnTrip, source } = useInference({
    maxCountries: 1,
  });

  return {
    isOnTrip,
    isProbablyOnTrip,
    status: result?.onTrip ?? null,
    isLoading,
    source,
  };
}

/**
 * Hook for just getting home country
 */
export function useHomeCountry(): {
  homeCountry: string | null;
  confidence: 'high' | 'medium' | 'low' | null;
  isLoading: boolean;
  source: 'cache' | 'computed' | null;
} {
  const { result, isLoading, homeCountry, source } = useInference({
    maxCountries: 1,
  });

  return {
    homeCountry,
    confidence: result?.overallConfidence ?? null,
    isLoading,
    source,
  };
}

/**
 * Hook for getting top recommended countries
 */
export function useTopCountries(count: number = 5): {
  countries: RecommendedCountry[];
  topCountry: string | null;
  isLoading: boolean;
  source: 'cache' | 'computed' | null;
} {
  const { result, isLoading, topCountry, source } = useInference({
    maxCountries: count,
  });

  return {
    countries: result?.relevantCountries ?? [],
    topCountry,
    isLoading,
    source,
  };
}

/**
 * Hook for checking if a specific country is recommended
 */
export function useIsCountryRecommended(countryCode: string): {
  isRecommended: boolean;
  rank: number | null;
  isLoading: boolean;
} {
  const { result, isLoading } = useInference();

  const country = result?.relevantCountries.find(
    (c) => c.countryCode.toUpperCase() === countryCode.toUpperCase()
  );

  return {
    isRecommended: !!country,
    rank: country?.rank ?? null,
    isLoading,
  };
}

// =============================================================================
// DEFAULT EXPORT
// =============================================================================

export default useInference;
